import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { isChunkLoadError, reloadOnceForStaleChunk } from '../lib/chunkReload';

/**
 * Per-route error boundary. Unlike the top-level ErrorBoundary
 * (which is full-screen and meant for catastrophic failures like a
 * crashed AuthProvider), this one stops the failure at the page
 * level so the surrounding chrome — sidebar nav, header, etc. —
 * remains usable. The operator can navigate to a different page
 * instead of being locked out of the whole app.
 *
 * Use with `key={location.pathname}` at the call site so that
 * navigating to a different route resets the boundary's state and
 * gives the new page a clean attempt at rendering.
 */

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, showDetails: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('RouteErrorBoundary caught:', error, info.componentStack);

    // A stale lazy-chunk import (an old hashed chunk removed by a newer
    // deploy) reaches us as a thrown error from React.lazy. It's
    // recoverable, not a real crash: try a one-time reload to pull the
    // fresh build, and do NOT report it to Sentry — capturing it just
    // floods the project with deploy-timing noise (the original
    // "Importing a module script failed" issue). The window-level
    // `vite:preloadError` handler in index.tsx is the primary recovery
    // path; this is the backstop for when the lazy() rejection surfaces
    // here without that event having fired.
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk();
      return;
    }

    // React's componentDidCatch swallows the throw before the global
    // window.onerror / unhandledrejection fires, so Sentry would
    // NOT see the crash without this explicit capture. We want
    // per-route crashes visible in monitoring even though the user
    // sees only the in-page fallback.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
      tags: { boundary: 'route' },
    });
  }

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            padding: 32,
            margin: 16,
            borderRadius: 12,
            background: 'rgba(255,107,107,0.08)',
            border: '1px solid rgba(255,107,107,0.25)',
            color: '#eaf0ff',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255,107,107,0.18)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
              fontSize: 22,
              fontWeight: 700,
            }}
          >
            !
          </div>
          <h2 style={{ fontSize: 18, margin: '0 0 8px', fontWeight: 700 }}>{t('error.routeTitle')}</h2>
          <p style={{ color: '#9fb0d7', fontSize: 13, margin: '0 0 20px', lineHeight: 1.6 }}>
            {t('error.routeDescription')}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                // If the user landed directly on this route (deep
                // link, password-reset email, etc.) there's no
                // history to go back to. Send them to /select-site
                // rather than "/" — "/" can redirect right back to
                // the same crashing route, creating an infinite
                // loop the user can't escape without typing a new
                // URL. /select-site is a safe stable landing.
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  window.location.href = '/select-site';
                }
              }}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                background: 'transparent',
                color: '#9fb0d7',
                border: '1px solid rgba(255,255,255,0.15)',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {t('error.goBack')}
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #2ee59d, #20c997)',
                color: '#070b14',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {t('error.reloadPage')}
            </button>
          </div>
          {this.state.error && (
            <div style={{ marginTop: 16 }}>
              {/* Pre-fix: raw "Request failed with status code 500"
                  rendered in a red <pre> by default — looks like the
                  app is leaking internals to a customer. Now hidden
                  behind a "Show technical details" disclosure that
                  defaults closed. The error is still sent to Sentry
                  via componentDidCatch regardless. */}
              <button
                type="button"
                onClick={() => this.setState({ showDetails: !this.state.showDetails })}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#9fb0d7',
                  fontSize: 11,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                {this.state.showDetails ? t('error.hideDetails') : t('error.showDetails')}
              </button>
              {this.state.showDetails && (
                <pre
                  style={{
                    marginTop: 8,
                    padding: 12,
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: 6,
                    fontSize: 10,
                    color: '#ff6b6b',
                    textAlign: 'left',
                    overflow: 'auto',
                    maxHeight: 100,
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  {this.state.error.message}
                </pre>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(RouteErrorBoundary);
